import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/** @typedef {{ id?: string, handle: string, title: string, image: string, price: string, url: string, available?: boolean, addedAt?: number }} WishlistItem */

const STORAGE_PREFIX = 'backtrack_wishlist_v1';

function config() {
  return window.BacktrackWishlist || {};
}

function storageKey() {
  const customer = config().customer || {};
  return `${STORAGE_PREFIX}::${customer.loggedIn ? customer.id : 'guest'}`;
}

/** @param {unknown} value */
function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] || char)
  );
}

function rootUrl() {
  const root = config().routes?.root || '/';
  return root.endsWith('/') ? root.slice(0, -1) : root;
}

/** @param {string} handle */
async function fetchProductByHandle(handle) {
  try {
    const response = await fetch(`${rootUrl()}/products/${handle}.js`, {
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    return null;
  }
}

export class WishlistChangeEvent extends Event {
  static eventName = 'wishlist:change';
  /** @param {WishlistItem[]} items */
  constructor(items) {
    super(WishlistChangeEvent.eventName, { bubbles: true });
    this.detail = { items };
  }
}

export const WishlistStore = {
  /** @returns {WishlistItem[]} */
  getAll() {
    try {
      const raw = localStorage.getItem(storageKey());
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  },

  /** @param {string} handle */
  has(handle) {
    return this.getAll().some((item) => item.handle === handle);
  },

  count() {
    return this.getAll().length;
  },

  /** @param {WishlistItem[]} items */
  save(items) {
    try {
      localStorage.setItem(storageKey(), JSON.stringify(items));
    } catch (error) {
      /* localStorage may be unavailable (private mode / quota exceeded) */
    }
    document.dispatchEvent(new WishlistChangeEvent(items));
    return items;
  },

  /** @param {WishlistItem} item */
  add(item) {
    const items = this.getAll();
    if (items.some((existing) => existing.handle === item.handle)) return items;
    const next = [{ ...item, addedAt: Date.now() }, ...items];
    return this.save(next);
  },

  /** @param {string} handle */
  remove(handle) {
    const next = this.getAll().filter((item) => item.handle !== handle);
    return this.save(next);
  },

  /** @param {WishlistItem} item */
  toggle(item) {
    if (this.has(item.handle)) {
      this.remove(item.handle);
      return false;
    }
    this.add(item);
    return true;
  },
};

/**
 * Heart toggle button rendered on product cards and product pages.
 * @extends {Component}
 */
class WishlistButton extends Component {
  requiredRefs = ['trigger'];

  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    document.addEventListener(WishlistChangeEvent.eventName, this.#boundSync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistChangeEvent.eventName, this.#boundSync);
  }

  #boundSync = () => this.#sync();

  toggle = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    const { customer } = config();

    if (!customer || !customer.loggedIn) {
      const dialog = document.getElementById('wishlist-login-dialog');
      if (dialog && typeof (/** @type {any} */ (dialog).showDialog) === 'function') {
        /** @type {any} */ (dialog).showDialog();
      }
      return;
    }

    const item = {
      id: this.dataset.productId || '',
      handle: this.dataset.productHandle || '',
      title: this.dataset.productTitle || '',
      image: this.dataset.productImage || '',
      price: this.dataset.productPrice || '',
      url: this.dataset.productUrl || '',
    };

    if (!item.handle) return;

    WishlistStore.toggle(item);
  };

  #sync() {
    const active = WishlistStore.has(this.dataset.productHandle || '');
    this.classList.toggle('is-active', active);

    const trigger = this.refs.trigger;
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = active
        ? this.dataset.labelRemove || 'Quitar de mi lista de deseos'
        : this.dataset.labelAdd || 'Añadir a mi lista de deseos';
      trigger.setAttribute('aria-label', label);
    }
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * @param {WishlistItem} item
 * @param {boolean} removable
 * @param {string} [variantId]
 */
function renderWishlistCard(item, removable, variantId) {
  const title = escapeHtml(item.title || '');
  const image = item.image || '';
  const price = item.price || '';
  const url = item.url || '#';
  const unavailable = item.available === false;

  return `
    <div class="wishlist-card${unavailable ? ' wishlist-card--unavailable' : ''}">
      <a href="${url}" class="wishlist-card__media">
        ${
          image
            ? `<img src="${image}" alt="${title}" loading="lazy" width="300" height="300">`
            : '<span class="wishlist-card__media-placeholder"></span>'
        }
        ${unavailable ? '<span class="wishlist-card__badge">Agotado</span>' : ''}
      </a>
      <div class="wishlist-card__info">
        <a href="${url}" class="wishlist-card__title">${title}</a>
        ${price ? `<span class="wishlist-card__price">${price}</span>` : ''}
      </div>
      ${
        removable
          ? `<button type="button" class="wishlist-card__remove" data-remove-handle="${escapeHtml(item.handle)}" aria-label="Quitar ${title} de mi lista">&times;</button>`
          : variantId && !unavailable
            ? `<button type="button" class="button button-secondary wishlist-card__add" data-add-to-cart-variant="${escapeHtml(variantId)}">Agregar al carrito</button>`
            : ''
      }
    </div>
  `;
}

/**
 * Renders the customer's saved products on the "Mi Lista de Deseos" page.
 * @extends {Component}
 */
class WishlistGrid extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#renderFromStore();
    document.addEventListener(WishlistChangeEvent.eventName, this.#boundRender);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistChangeEvent.eventName, this.#boundRender);
  }

  #boundRender = () => this.#renderFromStore();

  handleRemoveClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-remove-handle]') : null;
    if (!target) return;
    event.preventDefault();
    WishlistStore.remove(target.getAttribute('data-remove-handle') || '');
  };

  #renderFromStore() {
    const items = WishlistStore.getAll();
    this.#paint(items);
    this.#refreshLiveData(items);
  }

  /** @param {WishlistItem[]} items */
  #paint(items) {
    const empty = this.querySelector('[data-wishlist-empty]');
    const list = this.querySelector('[data-wishlist-list]');

    document.querySelectorAll('[data-wishlist-count]').forEach((node) => {
      node.textContent = String(items.length);
    });

    if (!list) return;

    if (items.length === 0) {
      list.innerHTML = '';
      list.setAttribute('hidden', '');
      empty?.removeAttribute('hidden');
      return;
    }

    empty?.setAttribute('hidden', '');
    list.removeAttribute('hidden');
    list.innerHTML = items.map((item) => renderWishlistCard(item, true)).join('');
  }

  /** @param {WishlistItem[]} items */
  async #refreshLiveData(items) {
    if (items.length === 0) return;

    const updates = await Promise.all(
      items.map(async (item) => {
        const product = await fetchProductByHandle(item.handle);
        if (!product) return { ...item, unavailable: true };

        const variant = product.variants && product.variants[0];
        const image = product.featured_image || (product.images && product.images[0]) || item.image;

        return {
          ...item,
          title: product.title || item.title,
          url: product.url || item.url,
          image: image ? `${image}${image.includes('?') ? '&' : '?'}width=400` : item.image,
          price: variant
            ? formatMoney(variant.price, config().moneyFormat || '${{amount}}', config().currency || 'USD')
            : item.price,
          available: variant ? variant.available : item.available,
        };
      })
    );

    const stillValid = updates.filter((item) => !item.unavailable);

    if (stillValid.length !== WishlistStore.getAll().length) {
      WishlistStore.save(stillValid);
      return;
    }

    this.#paint(stillValid);
  }
}

if (!customElements.get('wishlist-grid')) {
  customElements.define('wishlist-grid', WishlistGrid);
}

/**
 * Renders a read-only public version of a shared wishlist link.
 * @extends {Component}
 */
class WishlistPublicGrid extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  handleAddToCartClick = async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-add-to-cart-variant]') : null;
    if (!target) return;
    event.preventDefault();

    const variantId = target.getAttribute('data-add-to-cart-variant');
    if (!variantId) return;

    const originalText = target.textContent;
    target.setAttribute('disabled', '');
    target.textContent = 'Agregando…';

    try {
      const { routes } = config();
      const response = await fetch(routes?.cartAddUrl || `${rootUrl()}/cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      if (!response.ok) throw new Error('cart add failed');

      target.textContent = 'Agregado ✓';

      try {
        const cart = await (await fetch(`${rootUrl()}/cart.js`)).json();
        document.querySelectorAll('[data-testid="cart-bubble"]').forEach((el) => {
          el.textContent = String(cart.item_count);
        });
        document.querySelectorAll('.cart-bubble').forEach((el) => {
          el.classList.remove('visually-hidden');
        });
      } catch (refreshError) {
        /* non-critical: cart bubble will sync on next navigation */
      }

      setTimeout(() => {
        target.textContent = originalText;
        target.removeAttribute('disabled');
      }, 2200);
    } catch (error) {
      target.textContent = 'Error, intenta de nuevo';
      setTimeout(() => {
        target.textContent = originalText;
        target.removeAttribute('disabled');
      }, 2200);
    }
  };

  async #load() {
    const params = new URLSearchParams(window.location.search);
    const handles = (params.get('items') || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);

    const fromName = params.get('from');
    const heading = this.closest('.wishlist-page')?.querySelector('[data-wishlist-public-subtitle]');

    if (heading && fromName) {
      heading.textContent = `Estos son los discos que ${decodeURIComponent(fromName)} tiene guardados. ¡Hazle un regalo especial!`;
    }

    const empty = this.querySelector('[data-wishlist-empty]');
    const list = this.querySelector('[data-wishlist-list]');

    if (handles.length === 0) {
      empty?.removeAttribute('hidden');
      return;
    }

    const products = (await Promise.all(handles.map((handle) => fetchProductByHandle(handle)))).filter(Boolean);

    if (!list) return;

    if (products.length === 0) {
      empty?.removeAttribute('hidden');
      return;
    }

    list.removeAttribute('hidden');
    list.innerHTML = products
      .map((product) => {
        const variant = product.variants && product.variants[0];
        const image = product.featured_image || (product.images && product.images[0]);
        const item = {
          handle: product.handle,
          title: product.title,
          url: product.url,
          image: image ? `${image}${image.includes('?') ? '&' : '?'}width=400` : '',
          price: variant
            ? formatMoney(variant.price, config().moneyFormat || '${{amount}}', config().currency || 'USD')
            : '',
          available: variant ? variant.available : false,
        };
        return renderWishlistCard(item, false, variant ? String(variant.id) : undefined);
      })
      .join('');
  }
}

if (!customElements.get('wishlist-public-grid')) {
  customElements.define('wishlist-public-grid', WishlistPublicGrid);
}

/**
 * Lets the customer copy/share a link to their public wishlist.
 * @extends {Component}
 */
class WishlistShare extends Component {
  requiredRefs = ['trigger'];

  /** @type {number | undefined} */
  #announceTimeout;

  share = async (event) => {
    event?.preventDefault();

    const items = WishlistStore.getAll();
    if (items.length === 0) {
      this.#announce('Agrega al menos un disco a tu lista antes de compartirla.');
      return;
    }

    const handles = items.map((item) => item.handle).join(',');
    const baseUrl = this.dataset.baseUrl || '';
    const ownerName = this.dataset.ownerName || '';
    const url = `${baseUrl}?items=${encodeURIComponent(handles)}&from=${encodeURIComponent(ownerName)}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Lista de deseos de ${ownerName}`,
          text: 'Mira los discos que tengo guardados en mi lista de deseos',
          url,
        });
        return;
      } catch (error) {
        /* user dismissed the native share sheet, fall back to clipboard copy below */
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.#announce('¡Enlace copiado! Listo para compartir.');
    } catch (error) {
      this.#announce(url);
    }
  };

  /** @param {string} message */
  #announce(message) {
    const target = this.refs.successMessage;
    if (!(target instanceof HTMLElement)) return;

    target.textContent = message;
    target.removeAttribute('hidden');

    window.clearTimeout(this.#announceTimeout);
    this.#announceTimeout = window.setTimeout(() => {
      target.setAttribute('hidden', '');
    }, 4000);
  }
}

if (!customElements.get('wishlist-share')) {
  customElements.define('wishlist-share', WishlistShare);
}
